import { NgClass, NgTemplateOutlet } from '@angular/common';
import { Component, HostListener, OnInit, ViewChild, inject } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { MatSidenav, MatSidenavContainer, MatSidenavContent } from '@angular/material/sidenav';
import { ActivatedRoute, NavigationEnd, Router, RouterOutlet } from '@angular/router';
import { SwUpdate } from '@angular/service-worker';
import { Store, select } from '@ngrx/store';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { SwalComponent } from '@sweetalert2/ngx-sweetalert2';
import { BrowserStorageService, storageKeys } from 'core/browser-storage.service';
import { PushNotificationService } from 'core/push-notifications.service';
import { ThemeService } from 'core/theme.service';
import { EMPTY, getRandomInt } from 'core/utils';
import { Settings as LuxonSettings } from 'luxon';
import { interval, of } from 'rxjs';
import { exhaustMap, filter, take } from 'rxjs/operators';
import { AboutComponent } from 'shared/about/about.component';
import { InviteModalComponent } from 'shared/invite-modal/invite-modal.component';
import { selectLastApiError, selectLastBadRequestApiError, selectLastForbiddenApiError } from 'store/error/error.reducer';
import { selectActiveMatch } from 'store/matches/matches.reducers';
import { toggleSidebar } from 'store/sidebar/sidebar.actions';
import { environment } from '../environments/environment';
import { Session } from './core/session.service';
import { SignalRService } from './core/signalr.service';
import { AddNewMatchComponent } from './shared/add-new-match/add-new-match.component';
import { AllowPushNotificationsComponent } from './shared/alerts/allow-push-notifications/allow-push-notifications.component';
import { UpdateInProgressAlertComponent } from './shared/alerts/update-in-progress-alert/update-in-progress-alert.component';
import { BottomMenuBarComponent } from './shared/bottom-menu-bar/bottom-menu-bar.component';
import { SeasonSelectComponent } from './shared/season-select/season-select.component';
import { SidebarComponent } from './shared/sidebar/sidebar.component';
import { TopbarComponent } from './shared/topbar/topbar.component';
import { AppState, SeasonStore, matchesActions, sidebarActions } from './store';

@Component({
    selector: 'app-root',
    templateUrl: './app.component.html',
    styleUrls: ['./app.component.scss'],
    imports: [
        RouterOutlet,
        NgClass,
        NgTemplateOutlet,
        TopbarComponent,
        MatSidenavContainer,
        MatSidenavContent,
        AllowPushNotificationsComponent,
        UpdateInProgressAlertComponent,
        SeasonSelectComponent,
        MatSidenav,
        SidebarComponent,
        BottomMenuBarComponent,
        TranslateModule,
        TranslateModule,
        SwalComponent,
    ],
})
export class AppComponent implements OnInit {
    readonly session = inject(Session);
    private readonly swUpdate = inject(SwUpdate);
    private readonly store = inject<Store<AppState>>(Store);
    private readonly signalR = inject(SignalRService);
    private readonly dialog = inject(MatDialog);
    private readonly translate = inject(TranslateService);
    private readonly router = inject(Router);
    private readonly route = inject(ActivatedRoute);
    private readonly storage = inject(BrowserStorageService);
    private readonly pushService = inject(PushNotificationService);
    private readonly seasonStore = inject(SeasonStore);

    public readonly version = environment.version;

    public seasons = this.seasonStore.all;
    public showSeasonSelect = this.seasonStore.showSelect;
    public activeSeason = this.seasonStore.active;

    public appInstalled = false;
    public showReleaseNotesStrip = false;
    public currentModule: 'default' | 'widgets' | 'account' = 'default';

    public bottomMenuVisible = true;

    public sideBarOpened = false;
    public canSidebarBeOpened = true;

    public randomImageClass = `image-${getRandomInt(1, 7)}`;

    @ViewChild('apiErrorSwal')
    public readonly apiErrorSwal: SwalComponent;

    @ViewChild('apiForbiddenSwal')
    public readonly apiForbiddenSwal: SwalComponent;

    @ViewChild('apiBadRequestSwal')
    public readonly apiBadRequestSwal: SwalComponent;

    public apiBadRequestSwalText = '';

    public swalTraceId = 'No trace id provided';

    @HostListener('window:focus')
    public onWindowFocus() {
        this.signalR.checkConnection();
    }

    @HostListener('window:resize')
    public onWindowResize() {
        this.setSidebarState();
        this.canSidebarBeOpened = window.innerWidth > 1400;
    }
    constructor() {
        const store = this.store;
        const translate = this.translate;
        const themeService = inject(ThemeService);

        themeService.setInitialTheme();
        if (environment.version !== this.storage.local.getItem(storageKeys.version)) {
            this.showReleaseNotesStrip = true;
            this.storage.local.setItem(storageKeys.version, environment.version);
        }
        translate.onLangChange.subscribe(() => {
            const current = translate.currentLang;
            if (current.startsWith('cs')) {
                LuxonSettings.defaultLocale = 'cs';
            } else {
                LuxonSettings.defaultLocale = 'en';
            }
        });

        store.pipe(select((x) => x.sidebar.opened)).subscribe((opened) => {
            this.sideBarOpened = opened;
            this.setSidebarState();
        });

        translate.addLangs(['en', 'cs']);

        if (this.storage.local.getItem(storageKeys.showReleaseNotesStrip)) {
            this.showReleaseNotesStrip = true;
            this.storage.local.removeItem(storageKeys.showReleaseNotesStrip);
        }

        let lang = this.storage.local.getItem(storageKeys.lang);
        lang = this.translate.langs.find((x) => x === lang) || 'en';
        if (lang) {
            translate.use(lang);
        } else {
            const browserLang = translate.getBrowserCultureLang();
            if (browserLang.startsWith('cs')) {
                translate.use('cs');
            } else {
                translate.use('en');
            }
            this.storage.local.setItem(storageKeys.lang, translate.currentLang);
        }
    }

    ngOnInit() {
        this.canSidebarBeOpened = window.innerWidth > 1400;
        this.pushService.subscribeToNotificationActions();
        setTimeout(() => {
            this.store.dispatch(matchesActions.restoreActiveMatch());
        });

        this.session.userInitialized.pipe(take(1)).subscribe(() => {});
        this.handleAppUpdates();

        this.router.events.pipe(filter((x) => x instanceof NavigationEnd)).subscribe((e: NavigationEnd) => {
            if (e.url.startsWith('/widgets') || this.route.snapshot.queryParams['app'] === 'maui') {
                this.currentModule = 'widgets';
                return;
            }
            if (e.url.startsWith('/account')) {
                this.currentModule = 'account';
                return;
            }
            this.currentModule = 'default';
        });

        this.route.queryParams.subscribe((q) => {
            if (q.showSideBar) {
                this.store.dispatch(toggleSidebar());
            }
        });

        this.store
            .pipe(
                select(selectLastApiError),
                filter((x) => !!x),
                exhaustMap((err) => of(err)),
            )
            .subscribe((err) => {
                this.swalTraceId = `<span class="text-center text-muted text-small">Trace id: ${err.traceId}</span>`;
                setTimeout(() => {
                    this.apiErrorSwal.fire().then(EMPTY, EMPTY);
                });
            });
        this.store
            .pipe(
                select(selectLastForbiddenApiError),
                filter((x) => !!x),
                exhaustMap((err) => of(err)),
            )
            .subscribe(() => {
                setTimeout(() => {
                    this.apiForbiddenSwal.fire().then(EMPTY, EMPTY);
                });
            });
        this.store
            .pipe(
                select(selectLastBadRequestApiError),
                filter((x) => !!x),
                exhaustMap((err) => of(err)),
            )
            .subscribe((err) => {
                if (this.currentModule === 'account') {
                    return;
                }
                if (err.error) {
                    this.apiBadRequestSwalText = Object.values(err.error)
                        .map((value) => value.map((x) => String(this.translate.instant(x)) || x))
                        .reduce((a, b) => a.concat(b), [])
                        .join('<br />');
                }
                setTimeout(() => {
                    this.apiBadRequestSwal.fire().then(EMPTY, EMPTY);
                });
            });

        this.store
            .pipe(
                select(selectActiveMatch),
                filter((x) => x?.restored === true),
                take(1),
            )
            .subscribe((active) => {
                if (active) {
                    this.dialog.open(AddNewMatchComponent, {
                        position: { top: '20px' },
                        disableClose: true,
                        width: '95%',
                        maxWidth: '800px',
                    });
                }
            });
    }

    public showReleaseNotes() {
        this.dialog
            .open<AboutComponent>(AboutComponent, { maxWidth: '850px' })
            .afterClosed()
            .subscribe(() => {
                this.showReleaseNotesStrip = false;
            });
    }

    public openInviteModal() {
        this.dialog.open(InviteModalComponent, { autoFocus: false });
    }

    public onSidebarClosed() {
        if (this.canSidebarBeOpened) {
            this.store.dispatch(sidebarActions.closeSidebar());
        }
    }

    private handleAppUpdates() {
        if (!this.swUpdate.isEnabled) {
            return;
        }

        interval(10 * 60 * 1000).subscribe(() => {
            this.swUpdate.checkForUpdate().catch(EMPTY);
        });
        this.swUpdate.versionUpdates.subscribe((event) => {
            if (event.type === 'VERSION_READY') {
                this.swUpdate
                    .activateUpdate()
                    .then(() => document.location.reload())
                    .catch(EMPTY);
            }
        });
    }

    private setSidebarState() {
        const isOnNotificationRoute = this.router.url.startsWith('/notifications');
        if (!isOnNotificationRoute && this.sideBarOpened && !this.canSidebarBeOpened) {
            this.router.navigate(['/notifications']).catch(EMPTY);
        } else if (isOnNotificationRoute && (this.canSidebarBeOpened || !this.sideBarOpened)) {
            this.router.navigate(['/dashboard']).catch(EMPTY);
        }
    }
}
